import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Input } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import type {
  Service,
  CreateServiceRequest,
  UpdateServiceRequest,
  SubscriptionInterval,
  AssetValidationStatus,
  ThemeLayout,
  FallbackChainEntry,
} from '@/types/service';
import { EMPTY_ASSET_STATUS } from '@/types/service';
import { FallbackChainEditor, validateFallbackChain } from './fallback-chain-editor';
import { Combobox } from '@/components/ui/combobox';
import { useMerchants } from '@/hooks/use-merchants';
import { AlertTriangle, Info } from 'lucide-react';
import { FieldHelpPopover } from '@/components/ui/field-help-popover';
import { CARRIER_LOGO_OPTIONS } from '@/lib/utils/carrier-logos';
import { PaymentPagePreview } from './payment-page-preview';
import { PreviewDeviceToggle, type DeviceMode } from './preview-device-toggle';
import { AssetStatusIcon } from './asset-status-icon';

const INTERVALS: SubscriptionInterval[] = ['P1D', 'P1W', 'P1M', 'P3M', 'P6M', 'P1Y'];
const BUTTON_TEXT_IDS = ['1', '2', '3', '4'] as const;

/** Spec 0128: Client-seitige SSRF-Validierung fuer msisdnCollectionUrl */
function validateMsisdnUrl(url: string): string | null {
  if (!url.startsWith('https://')) return 'Nur HTTPS erlaubt';
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return 'Localhost nicht erlaubt';
    if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(host)) return 'Private IP nicht erlaubt';
    if (/^169\.254\./.test(host)) return 'Link-Local nicht erlaubt';
    return null;
  } catch { return 'Ungültige URL'; }
}

interface ServiceFormProps {
  /** Service to edit, or null for create mode */
  service?: Service | null;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Callback when form is submitted for creation */
  onSubmitCreate?: (data: CreateServiceRequest) => void;
  /** Callback when form is submitted for update */
  onSubmitUpdate?: (serviceId: string, data: UpdateServiceRequest) => void;
  /** Whether the submit operation is in progress */
  isSubmitting?: boolean;
}

/**
 * Form dialog for creating or editing a service.
 * Uses controlled inputs with validation.
 */
export function ServiceForm({
  service,
  open,
  onOpenChange,
  onSubmitCreate,
  onSubmitUpdate,
  isSubmitting = false,
}: ServiceFormProps) {
  const isEditMode = !!service;
  const { t } = useTranslation('services');
  // Merchants fuer Combobox im Create-Mode
  const { data: merchantsData, isLoading: merchantsLoading } = useMerchants({ status: 'active', limit: 100 });
  const merchantOptions = (merchantsData?.data ?? []).map((m) => ({
    value: m.merchantId,
    label: m.name,
    description: m.merchantId,
  }));
  // Form state
  const [merchantId, setMerchantId] = useState('');
  const [gatewayServiceId, setGatewayServiceId] = useState('');
  // Spec 0128: Fallback chain state
  const [fallbackChain, setFallbackChain] = useState<FallbackChainEntry[]>([
    { flowType: '' as FallbackChainEntry['flowType'], gatewayServiceId: '' },
  ]);
  const [isLegacy, setIsLegacy] = useState(false);
  const [showChainEditor, setShowChainEditor] = useState(false);
  const [description, setDescription] = useState('');
  const [grossAmount, setGrossAmount] = useState('');
  const [subscriptionInterval, setSubscriptionInterval] = useState<SubscriptionInterval>('P1W');
  const [buttonTextId, setButtonTextId] = useState<string>('1');
  const [stylesheetUrl, setStylesheetUrl] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [benefitText, setBenefitText] = useState('');
  const [carrierLogoId, setCarrierLogoId] = useState<string>('');
  const [themeLayout, setThemeLayout] = useState<string>('default'); // Spec 0087
  // Spec 0128: msisdnCollectionUrl state
  const [msisdnMode, setMsisdnMode] = useState<'facade' | 'custom'>('facade');
  const [msisdnCollectionUrl, setMsisdnCollectionUrl] = useState('');
  // Legal Links state (Spec 0040 M-6)
  const [agbUrl, setAgbUrl] = useState('');
  const [withdrawalUrl, setWithdrawalUrl] = useState('');
  const [imprintUrl, setImprintUrl] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [supportUrl, setSupportUrl] = useState('');
  // Preview state
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('mobile');
  const [assetStatus, setAssetStatus] = useState<AssetValidationStatus>(EMPTY_ASSET_STATUS);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when service changes or dialog opens
  useEffect(() => {
    if (open) {
      if (service) {
        setMerchantId(service.merchantId);
        setGatewayServiceId(service.gatewayServiceId);
        // Spec 0128: Legacy-Erkennung
        const legacy = !service.fallbackChain || service.fallbackChain.length === 0;
        setIsLegacy(legacy);
        if (!legacy && service.fallbackChain) {
          setFallbackChain(service.fallbackChain);
          setShowChainEditor(true);
        } else {
          setFallbackChain([{ flowType: '' as FallbackChainEntry['flowType'], gatewayServiceId: '' }]);
          setShowChainEditor(false);
        }
        // Spec 0128: msisdnCollectionUrl
        if (service.msisdnCollectionUrl) {
          setMsisdnMode('custom');
          setMsisdnCollectionUrl(service.msisdnCollectionUrl);
        } else {
          setMsisdnMode('facade');
          setMsisdnCollectionUrl('');
        }
        setDescription(service.description);
        setGrossAmount(String(service.grossAmount));
        setSubscriptionInterval(service.subscriptionInterval);
        setButtonTextId(String(service.theme?.buttonTextId ?? 1));
        setStylesheetUrl(service.theme?.stylesheetUrl ?? '');
        setHeaderImageUrl(service.theme?.headerImageUrl ?? '');
        setBackgroundImageUrl(service.theme?.backgroundImageUrl ?? '');
        setBenefitText(service.theme?.benefitText ?? '');
        setCarrierLogoId(service.theme?.complianceCarrierLogoId ? String(service.theme.complianceCarrierLogoId) : '');
        setThemeLayout(service.themeLayout ?? 'default');
        setAgbUrl(service.legalLinks?.agbUrl ?? '');
        setWithdrawalUrl(service.legalLinks?.withdrawalUrl ?? '');
        setImprintUrl(service.legalLinks?.imprintUrl ?? '');
        setPrivacyUrl(service.legalLinks?.privacyUrl ?? '');
        setSupportUrl(service.legalLinks?.supportUrl ?? '');
      } else {
        // Reset to defaults for create mode
        setMerchantId('');
        setGatewayServiceId('');
        setFallbackChain([{ flowType: '' as FallbackChainEntry['flowType'], gatewayServiceId: '' }]);
        setIsLegacy(false);
        setShowChainEditor(true); // Create mode always uses chain editor
        setMsisdnMode('facade');
        setMsisdnCollectionUrl('');
        setDescription('');
        setGrossAmount('');
        setSubscriptionInterval('P1W');
        setButtonTextId('1');
        setStylesheetUrl('');
        setHeaderImageUrl('');
        setBackgroundImageUrl('');
        setBenefitText('');
        setCarrierLogoId('');
        setThemeLayout('default');
        setAgbUrl('');
        setWithdrawalUrl('');
        setImprintUrl('');
        setPrivacyUrl('');
        setSupportUrl('');
      }
      setErrors({});
    }
  }, [open, service]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditMode) {
      if (!merchantId.trim()) {
        newErrors.merchantId = t('form.validation.merchantIdRequired');
      } else if (!/^[a-z0-9][a-z0-9-]{2,48}[a-z0-9]$/.test(merchantId)) {
        newErrors.merchantId = t('form.validation.merchantIdFormat');
      }
    }

    // Spec 0128: Chain-Validierung (Create + Edit mit Chain)
    if (showChainEditor) {
      const chainErrors = validateFallbackChain(fallbackChain);
      Object.assign(newErrors, chainErrors);
    } else if (!isEditMode) {
      // Legacy-Fallback: einzelne gatewayServiceId
      if (!gatewayServiceId.trim()) {
        newErrors.gatewayServiceId = t('form.validation.gatewayServiceIdRequired');
      } else if (!/^iq_svc_[a-zA-Z0-9]+$/.test(gatewayServiceId)) {
        newErrors.gatewayServiceId = t('form.validation.gatewayServiceIdFormat');
      }
    }

    if (!description.trim()) {
      newErrors.description = t('form.validation.descriptionRequired');
    } else if (description.length > 32) {
      newErrors.description = t('form.validation.descriptionMaxLength');
    }

    const amount = parseInt(grossAmount, 10);
    if (isNaN(amount) || amount < 1) {
      newErrors.grossAmount = t('form.validation.grossAmountPositive');
    }

    // Spec 0128: msisdnCollectionUrl SSRF validation
    if (showChainEditor && msisdnMode === 'custom' && msisdnCollectionUrl) {
      const urlErr = validateMsisdnUrl(msisdnCollectionUrl);
      if (urlErr) newErrors.msisdnCollectionUrl = urlErr;
    }

    // Validate URLs if provided
    const urlPattern = /^https:\/\/.+/;
    if (stylesheetUrl && !urlPattern.test(stylesheetUrl)) {
      newErrors.stylesheetUrl = t('form.validation.urlHttpsRequired');
    }
    if (headerImageUrl && !urlPattern.test(headerImageUrl)) {
      newErrors.headerImageUrl = t('form.validation.urlHttpsRequired');
    }
    if (backgroundImageUrl && !urlPattern.test(backgroundImageUrl)) {
      newErrors.backgroundImageUrl = t('form.validation.urlHttpsRequired');
    }

    // LegalLinks URL validation (Spec 0040)
    if (agbUrl && !urlPattern.test(agbUrl)) newErrors.agbUrl = t('form.validation.urlHttpsRequired');
    if (withdrawalUrl && !urlPattern.test(withdrawalUrl)) newErrors.withdrawalUrl = t('form.validation.urlHttpsRequired');
    if (imprintUrl && !urlPattern.test(imprintUrl)) newErrors.imprintUrl = t('form.validation.urlHttpsRequired');
    if (privacyUrl && !urlPattern.test(privacyUrl)) newErrors.privacyUrl = t('form.validation.urlHttpsRequired');
    if (supportUrl && !urlPattern.test(supportUrl)) newErrors.supportUrl = t('form.validation.urlHttpsRequired');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const hasThemeCustomization =
      buttonTextId !== '1' || stylesheetUrl || headerImageUrl || backgroundImageUrl ||
      benefitText || (carrierLogoId && carrierLogoId !== 'none');
    const theme = hasThemeCustomization
      ? {
          buttonTextId: parseInt(buttonTextId, 10) as 1 | 2 | 3 | 4,
          ...(stylesheetUrl && { stylesheetUrl }),
          ...(headerImageUrl && { headerImageUrl }),
          ...(backgroundImageUrl && { backgroundImageUrl }),
          ...(benefitText && { benefitText }),
          ...(carrierLogoId && carrierLogoId !== 'none' && { complianceCarrierLogoId: Number(carrierLogoId) }),
        }
      : undefined;

    // LegalLinks (Spec 0040 M-6) — nur senden wenn min. 1 URL gesetzt
    const hasLegalLinks = agbUrl || withdrawalUrl || imprintUrl || privacyUrl || supportUrl;
    const legalLinks = hasLegalLinks
      ? {
          ...(agbUrl && { agbUrl }),
          ...(withdrawalUrl && { withdrawalUrl }),
          ...(imprintUrl && { imprintUrl }),
          ...(privacyUrl && { privacyUrl }),
          ...(supportUrl && { supportUrl }),
        }
      : undefined;

    if (isEditMode && service) {
      const data: UpdateServiceRequest = {
        description,
        grossAmount: parseInt(grossAmount, 10),
        subscriptionInterval,
        themeLayout: themeLayout as ThemeLayout,
        ...(theme && { theme }),
        ...(legalLinks && { legalLinks }),
      };
      // Spec 0128: Nur Chain senden wenn Editor aktiv (kein Auto-Upgrade bei Legacy)
      if (showChainEditor) {
        data.fallbackChain = fallbackChain;
        data.msisdnCollectionUrl = msisdnMode === 'custom' ? msisdnCollectionUrl : null;
      }
      onSubmitUpdate?.(service.serviceId, data);
    } else {
      const data: CreateServiceRequest = {
        merchantId,
        description,
        grossAmount: parseInt(grossAmount, 10),
        currency: 'EUROCENT',
        subscriptionInterval,
        themeLayout: themeLayout as ThemeLayout,
        ...(theme && { theme }),
        ...(legalLinks && { legalLinks }),
      };
      // Spec 0128: Create sendet immer fallbackChain
      if (showChainEditor) {
        data.fallbackChain = fallbackChain;
        if (msisdnMode === 'custom' && msisdnCollectionUrl) {
          data.msisdnCollectionUrl = msisdnCollectionUrl;
        }
      } else {
        data.gatewayServiceId = gatewayServiceId;
      }
      onSubmitCreate?.(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[960px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl">
              {isEditMode ? t('form.title.edit') : t('form.title.create')}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? t('form.description.edit')
                : t('form.description.create')}
            </DialogDescription>
          </DialogHeader>

          <Separator className="mb-6" />

          {/* Split Layout: Form + Preview */}
          <div className="grid gap-8 md:grid-cols-[1fr,auto]">
            {/* Left: Form Fields */}
            <div className="space-y-6">
              {/* Section: Identity */}
              {/* Spec 0128: Warnhinweis bei aktiven Services */}
              {isEditMode && service?.status === 'active' && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Änderungen wirken sofort auf neue Transaktionen.</span>
                </div>
              )}

              {/* Section: Identity (Create-Mode: merchantId) */}
              {!isEditMode && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.section.identity')}
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="merchantId">
                      {t('form.label.merchantId')} <FieldHelpPopover helpKey="merchantId" /> <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      id="merchantId"
                      options={merchantOptions}
                      value={merchantId}
                      onChange={setMerchantId}
                      placeholder={t('form.placeholder.merchantId')}
                      searchPlaceholder="Merchant suchen…"
                      emptyText="Kein Merchant gefunden"
                      allowFreeText
                      loading={merchantsLoading}
                      error={!!errors.merchantId}
                    />
                    {errors.merchantId && (
                      <p id="merchantId-error" className="text-[13px] text-destructive">
                        {errors.merchantId}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Spec 0128: Legacy-Service Info + Migrate-Button */}
              {isEditMode && isLegacy && !showChainEditor && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-md border border-blue-500/50 bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950/20 dark:text-blue-200">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p>Dieser Service nutzt das alte Format mit einzelner Gateway-Service-ID.</p>
                      <p className="mt-1 font-mono text-xs">{service?.gatewayServiceId}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Migrate: Legacy-ID als ersten Chain-Eintrag, FlowType muss User waehlen
                      setFallbackChain([{
                        flowType: '' as FallbackChainEntry['flowType'],
                        gatewayServiceId: service?.gatewayServiceId ?? '',
                      }]);
                      setShowChainEditor(true);
                    }}
                  >
                    Zu Fallback-Kette migrieren
                  </Button>
                </div>
              )}

              {/* Spec 0128: Fallback-Chain Editor */}
              {showChainEditor && (
                <>
                  <FallbackChainEditor
                    value={fallbackChain}
                    onChange={setFallbackChain}
                    errors={errors}
                    disabled={isSubmitting}
                  />

                  {/* Spec 0128: msisdnCollectionUrl */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      MSISDN-Erfassung
                    </Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="msisdnMode"
                          value="facade"
                          checked={msisdnMode === 'facade'}
                          onChange={() => { setMsisdnMode('facade'); setMsisdnCollectionUrl(''); }}
                          disabled={isSubmitting}
                          className="accent-primary"
                        />
                        <span className="text-sm">Facade-eigene Seite (Standard)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="msisdnMode"
                          value="custom"
                          checked={msisdnMode === 'custom'}
                          onChange={() => setMsisdnMode('custom')}
                          disabled={isSubmitting}
                          className="accent-primary"
                        />
                        <span className="text-sm">Merchant-eigene URL</span>
                      </label>
                    </div>
                    {msisdnMode === 'custom' && (
                      <div className="space-y-1">
                        <Input
                          placeholder="https://merchant.example.com/msisdn"
                          value={msisdnCollectionUrl}
                          onChange={(e) => setMsisdnCollectionUrl(e.target.value)}
                          disabled={isSubmitting}
                          className={errors.msisdnCollectionUrl ? 'border-destructive' : ''}
                        />
                        {errors.msisdnCollectionUrl && (
                          <p className="text-[12px] text-destructive">{errors.msisdnCollectionUrl}</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Section: Service Details */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('form.section.details')}
                </h4>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">
                    {t('form.label.description')} <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder={t('form.placeholder.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={32}
                    rows={3}
                    aria-invalid={!!errors.description}
                    aria-describedby={errors.description ? 'description-error' : undefined}
                    className={errors.description ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    {errors.description ? (
                      <p id="description-error" className="text-destructive">
                        {errors.description}
                      </p>
                    ) : (
                      <span />
                    )}
                    <span className={description.length > 28 ? 'text-amber-500 font-medium' : ''}>
                      {description.length}/32
                    </span>
                  </div>
                </div>

                {/* Amount and Interval */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="grossAmount">
                      {t('form.label.grossAmount')} <FieldHelpPopover helpKey="grossAmount" /> <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="grossAmount"
                      type="number"
                      min="1"
                      placeholder={t('form.placeholder.grossAmount')}
                      value={grossAmount}
                      onChange={(e) => setGrossAmount(e.target.value)}
                      aria-invalid={!!errors.grossAmount}
                      aria-describedby={errors.grossAmount ? 'grossAmount-error' : undefined}
                      className={errors.grossAmount ? 'border-destructive focus-visible:ring-destructive' : ''}
                    />
                    {errors.grossAmount && (
                      <p id="grossAmount-error" className="text-[13px] text-destructive">
                        {errors.grossAmount}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subscriptionInterval">
                      {t('form.label.subscriptionInterval')} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={subscriptionInterval}
                      onValueChange={(v) => setSubscriptionInterval(v as SubscriptionInterval)}
                    >
                      <SelectTrigger id="subscriptionInterval">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERVALS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`interval.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section: Theme */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.section.theme')}
                  </h4>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {t('common:label.optional')}
                  </span>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="buttonTextId">{t('form.label.buttonTextId')}</Label>
                    <Select value={buttonTextId} onValueChange={setButtonTextId}>
                      <SelectTrigger id="buttonTextId">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUTTON_TEXT_IDS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`buttonText.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ThemeLayout (Spec 0087) */}
                  <div className="space-y-2">
                    <Label htmlFor="themeLayout" className="text-sm">
                      {t('form.label.themeLayout')}
                    </Label>
                    <Select value={themeLayout} onValueChange={setThemeLayout}>
                      <SelectTrigger id="themeLayout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">{t('form.option.themeLayout.default')}</SelectItem>
                        <SelectItem value="gameloft">{t('form.option.themeLayout.gameloft')}</SelectItem>
                        <SelectItem value="minimal">{t('form.option.themeLayout.minimal')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('form.help.themeLayout')}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground">{t('form.assetUrls')}</p>

                    <div className="space-y-2">
                      <Label htmlFor="stylesheetUrl" className="text-sm">
                        {t('form.label.stylesheetUrl')} <FieldHelpPopover helpKey="stylesheetUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="stylesheetUrl"
                          type="url"
                          placeholder="https://example.com/style.css"
                          value={stylesheetUrl}
                          onChange={(e) => setStylesheetUrl(e.target.value)}
                          className={`flex-1 ${errors.stylesheetUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.stylesheetUrl}
                          aria-describedby={errors.stylesheetUrl ? 'stylesheetUrl-error' : undefined}
                        />
                        <AssetStatusIcon status={assetStatus.stylesheet} />
                      </div>
                      {errors.stylesheetUrl && (
                        <p id="stylesheetUrl-error" className="text-[13px] text-destructive">
                          {errors.stylesheetUrl}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="headerImageUrl" className="text-sm">
                        {t('form.label.headerImageUrl')} <FieldHelpPopover helpKey="headerImageUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="headerImageUrl"
                          type="url"
                          placeholder="https://example.com/logo.png"
                          value={headerImageUrl}
                          onChange={(e) => setHeaderImageUrl(e.target.value)}
                          className={`flex-1 ${errors.headerImageUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.headerImageUrl}
                          aria-describedby={errors.headerImageUrl ? 'headerImageUrl-error' : undefined}
                        />
                        <AssetStatusIcon status={assetStatus.headerImage} />
                      </div>
                      {errors.headerImageUrl && (
                        <p id="headerImageUrl-error" className="text-[13px] text-destructive">
                          {errors.headerImageUrl}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="backgroundImageUrl" className="text-sm">
                        {t('form.label.backgroundImageUrl')} <FieldHelpPopover helpKey="backgroundImageUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="backgroundImageUrl"
                          type="url"
                          placeholder="https://example.com/background.jpg"
                          value={backgroundImageUrl}
                          onChange={(e) => setBackgroundImageUrl(e.target.value)}
                          className={`flex-1 ${errors.backgroundImageUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.backgroundImageUrl}
                          aria-describedby={
                            errors.backgroundImageUrl ? 'backgroundImageUrl-error' : undefined
                          }
                        />
                        <AssetStatusIcon status={assetStatus.backgroundImage} />
                      </div>
                      {errors.backgroundImageUrl && (
                        <p id="backgroundImageUrl-error" className="text-[13px] text-destructive">
                          {errors.backgroundImageUrl}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* BenefitText (Spec 0039 M-5) */}
                  <div className="space-y-2">
                    <Label htmlFor="benefitText" className="text-sm">
                      {t('form.label.benefitText')}
                    </Label>
                    <Textarea
                      id="benefitText"
                      placeholder={t('form.placeholder.benefitText')}
                      value={benefitText}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) setBenefitText(e.target.value);
                      }}
                      rows={2}
                      className={errors.benefitText ? 'border-destructive focus-visible:ring-destructive' : ''}
                      aria-invalid={!!errors.benefitText}
                      aria-describedby="benefitText-help"
                    />
                    <div className="flex items-center justify-between">
                      <p id="benefitText-help" className="text-xs text-muted-foreground">
                        {t('form.help.benefitText')}
                      </p>
                      <span className={`text-xs tabular-nums ${benefitText.length >= 200 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                        {benefitText.length}/200
                      </span>
                    </div>
                  </div>

                  {/* ComplianceCarrierLogoId (Spec 0039 M-5) */}
                  <div className="space-y-2">
                    <Label htmlFor="carrierLogoId" className="text-sm">
                      {t('form.label.carrierLogo')}
                    </Label>
                    <Select value={carrierLogoId || 'none'} onValueChange={setCarrierLogoId}>
                      <SelectTrigger id="carrierLogoId">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {CARRIER_LOGO_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={String(opt.id)}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('form.help.carrierLogo')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section: Legal Links (Spec 0040 M-6) */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('legalLinks.title')}
                  </h4>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {t('common:label.optional')}
                  </span>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                  <p className="text-xs text-muted-foreground">{t('legalLinks.formHint')}</p>

                  {([
                    { id: 'agbUrl', value: agbUrl, setter: setAgbUrl },
                    { id: 'withdrawalUrl', value: withdrawalUrl, setter: setWithdrawalUrl },
                    { id: 'imprintUrl', value: imprintUrl, setter: setImprintUrl },
                    { id: 'privacyUrl', value: privacyUrl, setter: setPrivacyUrl },
                    { id: 'supportUrl', value: supportUrl, setter: setSupportUrl },
                  ] as const).map(({ id, value, setter }) => (
                    <div key={id} className="space-y-1">
                      <Label htmlFor={id} className="text-sm">{t(`legalLinks.${id}`)}</Label>
                      <Input
                        id={id}
                        type="url"
                        placeholder="https://..."
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        className={errors[id] ? 'border-destructive focus-visible:ring-destructive' : ''}
                        aria-invalid={!!errors[id]}
                        aria-describedby={errors[id] ? `${id}-error` : undefined}
                      />
                      {errors[id] && (
                        <p id={`${id}-error`} className="text-[13px] text-destructive">{errors[id]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Preview */}
            <div className="hidden md:block">
              <div className="sticky top-0">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('preview')}
                  </span>
                  <PreviewDeviceToggle mode={deviceMode} onChange={setDeviceMode} />
                </div>
                <div className="rounded-lg border bg-muted/20 p-3">
                  <PaymentPagePreview
                    description={description}
                    grossAmount={parseInt(grossAmount, 10) || 0}
                    subscriptionInterval={subscriptionInterval}
                    theme={{
                      buttonTextId: parseInt(buttonTextId, 10) as 1 | 2 | 3 | 4,
                      stylesheetUrl: stylesheetUrl || undefined,
                      headerImageUrl: headerImageUrl || undefined,
                      backgroundImageUrl: backgroundImageUrl || undefined,
                    }}
                    deviceMode={deviceMode}
                    onAssetValidation={setAssetStatus}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="mt-6 mb-4" />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common:button.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {isEditMode ? t('common:button.save') : t('common:button.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
